import { useCallback, useMemo, useState } from "react";

import { useObjectMemo } from "../../../hooks";
import { ProductType } from "../../../../Account";

import { directDepositEnrollmentForm } from "./form";

import type Account from "../../../../Account";
import type { IconName } from "@narmi/design_system/dist/types/types/Icon.types";
import type { ReactLocalization } from "@fluent/react";

export const useDirectDepositManualForm = () => {
  const form = directDepositEnrollmentForm.useForm();
  return useObjectMemo({ form });
};

export const useSensitiveDataDisplay = (
  l10n: ReactLocalization,
  account?: Account | API.Account,
) => {
  const [isMasked, setIsMasked] = useState(true);
  const toggleMaskedState = () => setIsMasked((prev) => !prev);

  const formatSensitiveData = useCallback(
    (data: string | undefined | null) => {
      if (isMasked) {
        return "*********";
      }

      return data;
    },
    [isMasked],
  );

  const iconForShowHideButton: IconName = useMemo(
    () => (isMasked ? "eye" : "eye-off"),
    [isMasked],
  );

  const labelForShowHideButton = useMemo(
    () =>
      isMasked
        ? l10n.getString("direct-deposit-manual-form-show-info")
        : l10n.getString("direct-deposit-manual-form-hide-info"),
    [isMasked, l10n],
  );

  /**
   * For checking accounts we use the MICR number
   * For savings accounts we use the account number without a share ID
   */
  const accountNumberDisplay = useMemo(() => {
    if (!account) return "";

    if (account.product.type === ProductType.Savings) {
      return account.number.split("-")[0];
    }
    return account.check_micr;
  }, [account]);

  return useObjectMemo({
    isMasked,
    toggleMaskedState,
    formatSensitiveData,
    iconForShowHideButton,
    labelForShowHideButton,
    accountNumberDisplay,
  });
};
